import { useCallback, useEffect, useState } from 'react';
import useETagStore, { ETagMark, MARK_TTL, entityOf, firstETag, isSameVersion } from 'store/useETagStore';
import { useClient } from 'urql';
import { CompleteAction, CompleteType, IsCachedDocument } from 'generated/graphql';

const CACHED_POLL_INTERVAL = 5000;

const HELD_TYPES = [CompleteType.Hide, CompleteType.Stale, CompleteType.PreviewNext];

export interface CompleteActionOptions {
  /** How the button's action completes; undefined for a button with a choice of actions. */
  onComplete?: CompleteAction | null;
  /**
   * Keys the mark in the ETag store, so every button whose eTag names the same entity shares it and
   * it outlives the button. A button without one holds its mark while it is mounted.
   */
  eTag?: string | string[];
  /** Should be stable: each runs whenever it changes, as well as when what it reports changes. */
  onHide?: (hidden: boolean) => void;
  onStale?: (stale: boolean) => void;
}

/**
 * What a button shows once its action has completed with Hide, Stale or PreviewNext: it holds a
 * mark until the change lands, the row moves to a new version, the mark's TTL runs out, the action
 * or the action errors. A Stale mark polls isCached for the eTag it was made against.
 */
const useCompleteAction = ({ onComplete, eTag, onHide, onStale }: CompleteActionOptions) => {
  const client = useClient();
  const storeKey = entityOf(eTag);
  const storedMark = useETagStore((store) => (storeKey ? store.marks[storeKey] : undefined));
  const [localMark, setLocalMark] = useState<ETagMark>();

  const mark = storeKey ? storedMark : localMark;
  const held = mark && (!storeKey || isSameVersion(mark, eTag)) ? mark : undefined;
  const isHeld = Boolean(held);
  const isHidden = held?.onComplete.type === CompleteType.Hide;
  const isStale = held?.onComplete.type === CompleteType.Stale;
  const nextLabel =
    held?.onComplete.type === CompleteType.PreviewNext ? held.onComplete.nextLabel || undefined : undefined;
  const waitingOn = isStale ? firstETag(held?.eTag) : undefined;

  const clear = useCallback(
    () => (storeKey && eTag ? useETagStore.getState().clear(eTag) : setLocalMark(undefined)),
    [storeKey, eTag],
  );

  const complete = () => {
    if (!onComplete) return;
    if (onComplete.type === CompleteType.Ignore) {
      clear();
      return;
    }
    if (!HELD_TYPES.includes(onComplete.type)) return;
    if (storeKey && eTag) useETagStore.getState().mark(eTag, onComplete);
    else setLocalMark({ eTag: eTag ?? '', onComplete, time: Date.now() });
  };

  // A button reports only once it has held a mark. One that never has has nothing to tell the row,
  // and reporting false as it mounts would undo a mark that another button on the row holds.
  const [hasHeld, setHasHeld] = useState(false);
  if (isHeld && !hasHeld) setHasHeld(true);
  useEffect(() => {
    if (hasHeld) onHide?.(isHidden);
  }, [hasHeld, isHidden, onHide]);
  useEffect(() => {
    if (hasHeld) onStale?.(isStale);
  }, [hasHeld, isStale, onStale]);

  // A mark made against an older version of the row is done with: the row has moved on.
  useEffect(() => {
    if (storeKey && storedMark && !isSameVersion(storedMark, eTag)) clear();
  }, [storeKey, storedMark, eTag, clear]);

  useEffect(() => {
    if (!localMark) return;
    const timer = setTimeout(() => setLocalMark(undefined), MARK_TTL - (Date.now() - localMark.time));
    return () => clearTimeout(timer);
  }, [localMark]);

  useEffect(() => {
    if (!waitingOn) return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout>;
    const poll = async () => {
      const result = await client
        .query(IsCachedDocument, { eTag: waitingOn }, { requestPolicy: 'network-only' })
        .toPromise();
      if (cancelled) return;
      // An error is no answer: keep asking, and leave giving up to the mark's TTL.
      if (result.data?.isCached === false) {
        clear();
        return;
      }
      timer = setTimeout(poll, CACHED_POLL_INTERVAL);
    };
    timer = setTimeout(poll, CACHED_POLL_INTERVAL);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [waitingOn, client, clear]);

  return { nextLabel, isDisabled: isHeld, complete, clear };
};

export default useCompleteAction;
